import { type FC, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui';
import { Input } from '@/components/ui';
import { Label } from '@/components/ui';
import { Button } from '@/components/ui';
import { useAuth } from '@/hooks';

/**
 * Registration page with sign-up form
 */
export const RegisterPage: FC = () => {
  const navigate = useNavigate();
  const { signUp, loading, error } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [club, setClub] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setFormError(null);

    // Validation
    if (!email || !password || !confirmPassword || !firstName || !lastName || !club) {
      setFormError('Please fill in all required fields');
      return;
    }

    if (!email.includes('@')) {
      setFormError('Please enter a valid email address');
      return;
    }

    if (password.length < 8) {
      setFormError('Password must be at least 8 characters long');
      return;
    }

    if (password !== confirmPassword) {
      setFormError('Passwords do not match');
      return;
    }

    try {
      await signUp(email, password, {
        first_name: firstName,
        last_name: lastName,
        club: club,
      });

      // Supabase sends the confirmation email automatically
      navigate('/login', {
        state: { message: 'Account created! Please check your email to verify your account.' },
      });
    } catch (err) {
      setFormError(
        err instanceof Error ? err.message : 'Failed to create account. Please try again.'
      );
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-md mx-auto">
          <Card className="bg-white border-turquoise-200 shadow-md">
          <CardHeader className="text-center">
            <CardTitle className="text-3xl font-viking text-turquoise-900">Create Account</CardTitle>
            <CardDescription className="text-turquoise-700">Join the Cactus Cup community</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Error message */}
              {(formError || error) && (
                <div className="bg-red-900/40 text-red-200 text-sm p-3 rounded-md border border-red-500/40">
                  {formError || error?.message}
                </div>
              )}

              {/* Name fields */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName" required className="text-turquoise-900">
                    First Name
                  </Label>
                  <Input
                    id="firstName"
                    type="text"
                    placeholder="John"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    disabled={loading}
                    autoComplete="given-name"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName" required className="text-turquoise-900">
                    Last Name
                  </Label>
                  <Input
                    id="lastName"
                    type="text"
                    placeholder="Doe"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    disabled={loading}
                    autoComplete="family-name"
                    required
                  />
                </div>
              </div>

              {/* Club Affiliation field */}
              <div className="space-y-2">
                <Label htmlFor="club" required className="text-turquoise-900">
                  Club Affiliation
                </Label>
                <Input
                  id="club"
                  type="text"
                  placeholder="Your HEMA club or school"
                  value={club}
                  onChange={(e) => setClub(e.target.value)}
                  disabled={loading}
                  autoComplete="organization"
                  required
                />
              </div>

              {/* Email field */}
              <div className="space-y-2">
                <Label htmlFor="email" required className="text-turquoise-900">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your.email@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  autoComplete="email"
                  required
                />
              </div>

              {/* Password field */}
              <div className="space-y-2">
                <Label htmlFor="password" required className="text-turquoise-900">
                  Password
                </Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  autoComplete="new-password"
                  required
                />
                <p className="text-xs text-turquoise-600">Must be at least 8 characters long</p>
              </div>

              {/* Confirm password field */}
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" required className="text-turquoise-900">
                  Confirm Password
                </Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={loading}
                  autoComplete="new-password"
                  required
                />
              </div>

              {/* Submit button */}
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Creating account...' : 'Create Account'}
              </Button>

              {/* Login link */}
              <div className="text-center text-sm text-turquoise-700">
                Already have an account?{' '}
                <Link to="/login" className="text-orange-600 hover:text-orange-700 font-medium">
                  Sign in
                </Link>
              </div>
            </form>
          </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
